import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/** Adds only manually selected accessories; the main product form stays independent. */
class RCProductAccessories extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.rows = [...this.querySelectorAll('[data-rc-accessory-row]')];
    this.button = this.querySelector('[data-rc-accessories-submit]');
    this.total = this.querySelector('[data-rc-accessories-total]');
    this.status = this.querySelector('[data-rc-accessories-status]');
    this.cartLink = this.querySelector('[data-rc-accessories-cart]');
    this.busy = false;
    this.added = false;

    this.addEventListener('change', (event) => {
      const row = event.target.closest?.('[data-rc-accessory-row]');
      if (!row || this.busy) return;
      this.added = false;
      this.#setStatus('');
      this.#updateRow(row, event.target.matches('[data-rc-accessory-variant]'));
      this.#render();
    }, { signal });
    this.addEventListener('input', (event) => {
      if (!event.target.matches?.('[data-rc-accessory-quantity]') || this.busy) return;
      this.added = false;
      this.#setStatus('');
      this.#render();
    }, { signal });
    this.button?.addEventListener('click', (event) => {
      event.preventDefault();
      this.#submit();
    }, { signal });
    document.addEventListener('rancold:vat-change', () => this.#render(), { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then((result) => {
        if (this.isConnected && Array.isArray(result.detail?.items)) {
          this.#syncCart({ items: result.detail.items });
        }
      }).catch(() => {});
    }, { signal });
    this.rows.forEach((row) => this.#updateRow(row));
    this.#render();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  #parts(row) {
    const select = row.querySelector('[data-rc-accessory-variant]');
    return {
      select,
      option: select?.selectedOptions[0],
      input: row.querySelector('[data-rc-accessory-quantity]'),
      checkbox: row.querySelector('[data-rc-accessory-select]'),
    };
  }

  #updateRow(row, resetQuantity = false) {
    const { select, option, input, checkbox } = this.#parts(row);
    if (!option || !input || !checkbox) return;
    const minimum = Math.max(1, Number(option.dataset.min) || 1);
    const increment = Math.max(1, Number(option.dataset.increment) || 1);
    const maximum = option.dataset.max === '' || option.dataset.max == null ? null : Number(option.dataset.max);
    const cartQuantity = Number(option.dataset.cartQuantity) || 0;
    const available = option.dataset.available === 'true';
    input.min = String(minimum);
    input.step = String(increment);
    if (Number.isFinite(maximum)) input.max = String(Math.max(0, maximum - cartQuantity));
    else input.removeAttribute('max');
    if (resetQuantity || !input.value) input.value = String(minimum);
    input.setCustomValidity('');
    if (!available) checkbox.checked = false;
    row.classList.toggle('is-selected', checkbox.checked);
    checkbox.disabled = this.busy || !available;
    input.disabled = this.busy || !available || !checkbox.checked;
    // Unavailable selections must still allow choosing another variant.
    select.disabled = this.busy;

    const price = row.querySelector('[data-rc-accessory-price]');
    if (price) {
      price.dataset.rcPriceExcl = option.dataset.priceExcl || '';
      price.dataset.rcPriceIncl = option.dataset.priceIncl || '';
      price.textContent = window.RancoldVAT?.getMode() === 'incl'
        ? option.dataset.priceIncl || '' : option.dataset.priceExcl || '';
    }
    const stock = row.querySelector('[data-rc-accessory-stock]');
    if (stock) stock.textContent = option.dataset.stockLabel || '';
    const rules = row.querySelector('[data-rc-accessory-rules]');
    const template = Number.isFinite(maximum)
      ? rules?.dataset.template : rules?.dataset.templateUnlimited || rules?.dataset.template;
    if (rules && template) {
      rules.textContent = template.replaceAll('{minimum}', String(minimum))
        .replaceAll('{increment}', String(increment)).replaceAll('{maximum}', maximum == null ? '' : String(maximum));
    }
    window.RancoldVAT?.apply(row);
  }

  #selected() {
    return this.rows.flatMap((row) => {
      const { option, input, checkbox } = this.#parts(row);
      if (!option || !input || !checkbox?.checked) return [];
      return [{
        row,
        input,
        variantId: option.value,
        quantity: Number(input.value),
        minimum: Math.max(1, Number(option.dataset.min) || 1),
        increment: Math.max(1, Number(option.dataset.increment) || 1),
        maximum: option.dataset.max === '' || option.dataset.max == null ? null : Number(option.dataset.max),
        cartQuantity: Number(option.dataset.cartQuantity) || 0,
        available: option.dataset.available === 'true',
      }];
    });
  }

  #validate(selected) {
    const totals = new Map();
    for (const item of selected) {
      if (!item.available || !/^\d+$/.test(item.variantId) || !Number.isSafeInteger(item.quantity)
        || item.quantity < item.minimum || (item.quantity - item.minimum) % item.increment !== 0) return item;
      totals.set(item.variantId, (totals.get(item.variantId) || 0) + item.quantity);
    }
    return selected.find((item) => Number.isFinite(item.maximum)
      && item.cartQuantity + totals.get(item.variantId) > item.maximum);
  }

  #render() {
    if (!this.rows || !this.button) return;
    this.rows.forEach((row) => this.#updateRow(row));
    const selected = this.#selected();
    let cents = 0;
    for (const item of selected) {
      const { option } = this.#parts(item.row);
      cents += (Number(option?.dataset.price) || 0) * Math.max(0, Number.isFinite(item.quantity) ? item.quantity : 0);
    }
    if (this.total) {
      const multiplier = Number(this.dataset.vatMultiplier) || 1;
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'sv-SE', {
        style: 'currency', currency: this.dataset.currency || 'SEK',
      });
      this.total.dataset.rcPriceExcl = formatter.format(cents / 100);
      this.total.dataset.rcPriceIncl = formatter.format(Math.round(cents * multiplier) / 100);
      this.total.textContent = window.RancoldVAT?.getMode() === 'incl'
        ? this.total.dataset.rcPriceIncl : this.total.dataset.rcPriceExcl;
      window.RancoldVAT?.apply(this.total);
    }
    this.button.disabled = this.busy || selected.length === 0;
    this.button.textContent = this.busy ? this.dataset.labelBusy
      : this.added ? this.dataset.labelAdded : this.dataset.labelReady;
    this.setAttribute('aria-busy', String(this.busy));
  }

  #setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message || '';
    this.status.dataset.state = isError ? 'error' : 'success';
  }

  #syncCart(cart) {
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
    for (const row of this.rows) {
      for (const option of row.querySelectorAll('[data-rc-accessory-variant] option')) {
        option.dataset.cartQuantity = String(cart.items.reduce((total, item) => total
          + (String(item.variant_id) === option.value ? Number(item.quantity) || 0 : 0), 0));
      }
    }
    this.#render();
  }

  async #fetchCart() {
    const nativeCart = document.querySelector('cart-items-component');
    if (typeof nativeCart?.fetchCartData === 'function') return nativeCart.fetchCartData();
    const root = window.Shopify?.routes?.root || '/';
    const cartRoute = typeof Theme !== 'undefined' ? Theme.routes.cart_url : `${root}cart`;
    const response = await fetch(`${cartRoute}.json`, {
      headers: { Accept: 'application/json' }, credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  async #resolveCart(deferred, response, didError, itemCount) {
    const cart = await this.#fetchCart();
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items, sections: response?.sections, didError,
        source: 'rc-accessories', sourceId: this.id, itemCount,
      },
    });
    if (this.isConnected) this.#syncCart(cart);
  }

  async #submit() {
    if (this.busy) return;
    const selected = this.#selected();
    if (!selected.length) return;
    let invalid = this.#validate(selected);
    if (invalid) {
      this.#setStatus(this.dataset.labelQuantityError || this.dataset.labelError, true);
      invalid.input.reportValidity();
      return;
    }
    this.busy = true;
    this.added = false;
    this.#setStatus('');
    this.#render();
    let deferred;
    let mutationStarted = false;
    let responseData;
    const totalQuantity = selected.reduce((sum, item) => sum + item.quantity, 0);
    try {
      // Recheck combined limits against the current cart before changing it.
      const beforeCart = await this.#fetchCart();
      this.#syncCart(beforeCart);
      for (const item of selected) {
        item.cartQuantity = beforeCart.items.reduce((total, line) => total
          + (String(line.variant_id) === item.variantId ? Number(line.quantity) || 0 : 0), 0);
      }
      invalid = this.#validate(selected);
      if (invalid) {
        this.#setStatus(this.dataset.labelQuantityError || this.dataset.labelError, true);
        return;
      }
      const quantities = new Map();
      for (const item of selected) quantities.set(item.variantId, (quantities.get(item.variantId) || 0) + item.quantity);
      const items = [...quantities].map(([id, quantity]) => ({ id: Number(id), quantity }));
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
        .map((component) => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      }));
      const root = window.Shopify?.routes?.root || '/';
      const addRoute = typeof Theme !== 'undefined' ? Theme.routes.cart_add_url : `${root}cart/add.js`;
      mutationStarted = true;
      const response = await fetch(addRoute, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: `${window.location.pathname}${window.location.search}` }),
      });
      responseData = await response.json();
      if (!response.ok || responseData.status) {
        const message = responseData.description || responseData.message || this.dataset.labelError;
        this.dispatchEvent(new CartErrorEvent({
          error: message, code: 'INVALID', detail: { description: message, errors: responseData.errors },
        }));
        await this.#resolveCart(deferred, responseData, true, totalQuantity);
        this.#setStatus(message, true);
        this.cartLink?.removeAttribute('hidden');
        return;
      }
      await this.#resolveCart(deferred, responseData, false, totalQuantity);
      if (this.isConnected) {
        for (const item of selected) this.#parts(item.row).checkbox.checked = false;
        this.added = true;
        this.#setStatus(this.dataset.labelAdded);
        this.cartLink?.removeAttribute('hidden');
      }
    } catch (error) {
      if (mutationStarted) {
        // Errors can follow a partial/successful mutation. Refresh, never retry automatically.
        this.dispatchEvent(new CartErrorEvent({ error: this.dataset.labelUncertain, code: 'SERVICE_UNAVAILABLE' }));
        try {
          await this.#resolveCart(deferred, responseData, true, totalQuantity);
        } catch (refreshError) {
          deferred?.reject(refreshError);
        }
        this.#setStatus(this.dataset.labelUncertain || this.dataset.labelError, true);
        this.cartLink?.removeAttribute('hidden');
      } else {
        this.#setStatus(this.dataset.labelError, true);
      }
    } finally {
      this.busy = false;
      if (this.isConnected) this.#render();
    }
  }
}

if (!customElements.get('rc-product-accessories')) {
  customElements.define('rc-product-accessories', RCProductAccessories);
}
